import { FixedIncomeOfferBaseEntity, type FixedIncomePositionEntity } from '@genial/services';
import { Utils } from '@genial/utils';

import { handleOrderByProps } from '../../constants';

type FixedIncomeTableKey =
  | 'productDetails'
  | 'productDisplay'
  | 'maturityDate'
  | 'profitabilityFormatted'
  | 'suitabilityProfileName'
  | 'riskRating'
  | 'unitPrice'
  | 'inputApplicationAmount'
  | 'buttonSelectProduct';

type FixedIncomeRescueTableKey =
  | 'productDisplay'
  | 'availableQuantity'
  | 'IR'
  | 'netValue'
  | 'quantityRescue'
  | 'availableAccount'
  | 'buttonSelectProduct';

export type FixedIncomeTableItemProps = Record<FixedIncomeTableKey, string> &
  Partial<{
    qualifiedInvestor: boolean;
    dailyLiquidity: boolean;
    fgcProtected: boolean;
    taxExempt: boolean;
    productId: string;
    applicationMinimumAmount: number;
    applicationMaximumAmount: number;
    unitPrice: number | string;
    dueDate: Date;
  }>;

export type FixedIncomeRescueTableItemProps = Record<
  FixedIncomeRescueTableKey,
  number | string | null
> &
  FixedIncomePositionEntity;

export type Offer = FixedIncomeOfferBaseEntity & FixedIncomePositionEntity;

export interface FixedIncomeTableProps {
  OffersAll?: Offer[];
  OffersSearchTerm?: Offer[];
  onOpenDetailsModal?: (data: Offer & { marketStatus?: string; isMarketClosed?: boolean }) => void;
  isLoading: boolean;
  isLoadingMore?: boolean;
  handleEndReached?: () => void;
  onChangeMessageAdjustUnitPrice?: (value: boolean) => void;
  marketIsClosed: boolean;
  marketStatus?: string;
  invalidColumns?: boolean;
  handleClearSearch?: () => void;
  handleOrderBy?: handleOrderByProps;
  hasEmpty: boolean;
}

export const tableHeadRescue: Partial<FixedIncomeRescueTableItemProps> = {
  productDisplay: 'Produto',
  availableQuantity: 'Quantidade disponível',
  IR: 'IR',
  netValue: 'Valor líquido',
  quantityRescue: 'Quantidade a resgatar',
  availableAccount: 'Disponível em conta',
  buttonSelectProduct: '',
};

export const tableHeadApply: Partial<FixedIncomeTableItemProps> = {
  productDetails: '',
  productDisplay: 'Produto',
  maturityDate: 'Vencimento',
  profitabilityFormatted: 'Rentabilidade',
  suitabilityProfileName: 'Risco',
  riskRating: 'Rating (BR)',
  unitPrice: 'Preço unitário',
  inputApplicationAmount: 'Valor a aplicar',
  buttonSelectProduct: '',
};

export const orderByData = [
  {
    label: 'Produto',
    value: 'productDisplay',
  },
  {
    label: 'Vencimento',
    value: 'maturityDate',
  },
  {
    label: 'Rentabilidade',
    value: 'profitabilityFormatted',
  },
  {
    label: 'Risco',
    value: 'suitabilityProfileName',
  },
  {
    label: 'Rating (BR)',
    value: 'riskRating',
  },
  {
    label: 'Preço unitário',
    value: 'unitPrice',
  },
];

export const selectedKeysOrderBy = orderByData.map((item) => item.value);

export const tableParse = (item: Offer) => {
  return {
    ...item,
    productDisplay: item.productDisplay || '',
    profitabilityFormatted: item.profitabilityFormatted || '',
    suitabilityProfileName: item.suitabilityProfileName || '',
    riskRating: item.riskRating || '',
    productId: String(item.productId),
    productDetails: 'Veja mais detalhes\nsobre este produto',
    maturityDate: Utils.Mask.DateFormat(item.maturityDate as unknown as Date, 'dd/MM/yyyy'),
    unitPrice: Utils.Mask.Money(item.unitPrice),
    inputApplicationAmount: Utils.Mask.Money(item.applicationMinimumAmount) as string,
    buttonSelectProduct: 'Investir',
  } as unknown as Partial<FixedIncomeTableItemProps>;
};

export const messageError = '--';
export const formatAvailableAmount = (value: number) =>
  `${String(value ?? messageError).replace('.', ',')} unidades`;
export const tableParseRescue = (item: Offer): Partial<FixedIncomeRescueTableItemProps> => {
  return {
    ...item,
    productDisplay: item.productDisplay || '',
    availableQuantity: formatAvailableAmount(item.availableAmount),
    IR: Utils.Mask.Money(item.ir),
    netValue: Utils.Mask.Money(item.netFinancialValue),
    quantityRescue: item.availableAmount,
    availableAccount:
      item.redemptionDate && Utils.Mask.DateFormat(item.redemptionDate, 'dd/MM/yyyy'),
    buttonSelectProduct: 'RESGATAR',
  };
};

export const widthCols: Record<FixedIncomeTableKey, number> = {
  productDetails: 3,
  productDisplay: 17,
  maturityDate: 11,
  profitabilityFormatted: 11,
  suitabilityProfileName: 11,
  riskRating: 10,
  unitPrice: 11,
  inputApplicationAmount: 15,
  buttonSelectProduct: 11,
};

export const fixedIncomeRescueWidthCols: Record<FixedIncomeRescueTableKey, number> = {
  productDisplay: 17,
  availableQuantity: 15,
  IR: 15,
  netValue: 13,
  quantityRescue: 15,
  availableAccount: 14,
  buttonSelectProduct: 11,
};

export const invalidColumnsCalc =
  Object.entries(widthCols).reduce((acc, item) => acc + item[1], 0) !== 100;

export type FormData = { createAppliedAmount: string };

export const getRiskRatingLevelValue = (value: number) => {
  const intervals: { [key: string]: number } = {
    '0 - 1.5': 1,
    '1.5 - 2.9': 2,
    '2.9 - 4': 3,
    '4 - 5.5': 4,
    '5.5 - ∞': 5,
  };

  for (const interval in intervals) {
    const [min, max] = interval.split(' - ').map(Number);
    if (value > min && value <= max) {
      return intervals[interval];
    }
  }

  return 0;
};

export type FixedIncomeOperationType = 'apply' | 'rescue';
